import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import { useTheme } from "../DataContext";
import { DEMO_RESOURCES } from "../demoData";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, ZAxis, Legend, Cell,
} from "recharts";

export default function ResourceIntelligence() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(null);

  const bottlenecks   = DEMO_RESOURCES.filter(r => r.bottleneck);
  const overAllocated = DEMO_RESOURCES.filter(r => r.utilization > 100);
  const avgUtil       = Math.round(DEMO_RESOURCES.reduce((s, r) => s + r.utilization, 0) / DEMO_RESOURCES.length);
  const avgEff        = Math.round(DEMO_RESOURCES.reduce((s, r) => s + r.efficiency, 0) / DEMO_RESOURCES.length);

  const utilizationColor = (u) => u > 100 ? theme.danger : u > 85 ? theme.warning : u < 50 ? theme.textMuted : theme.success;

  const scatterData = DEMO_RESOURCES.map(r => ({
    x: r.utilization,
    y: r.efficiency,
    z: r.projects * 20,
    name: r.name,
    bottleneck: r.bottleneck,
  }));

  const radarData = selected ? [
    { subject: "Utilization", A: selected.utilization },
    { subject: "Efficiency",  A: selected.efficiency  },
    { subject: "Cost Fit",    A: 100 - ((selected.cost_rate - 95) / 50 * 100) },
    { subject: "Availability",A: 100 - selected.utilization + 20 },
    { subject: "Multi-Project",A: selected.projects * 40 },
  ] : [];

  return (
    <PageLayout
      title="Resource Intelligence"
      subtitle="Utilization heatmaps, bottleneck prediction, efficiency scoring — P-05"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Total Resources"   value={DEMO_RESOURCES.length} icon="◉" color={theme.accent}  sub="Across portfolio" />
        <KPICard label="Bottlenecks"       value={bottlenecks.length}    icon="⚠" color={theme.danger}  sub="Capacity constraint" />
        <KPICard label="Over-Allocated"    value={overAllocated.length}  icon="▲" color={theme.warning} sub=">100% utilization" />
        <KPICard label="Avg Utilization"   value={`${avgUtil}%`}         icon="◈" color={utilizationColor(avgUtil)} sub={`Avg efficiency: ${avgEff}%`} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "20px", marginBottom: "20px" }}>
        {/* Utilization + Efficiency Bar */}
        <div className="pmo-card">
          <div className="section-title">Resource Utilization vs Efficiency</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={DEMO_RESOURCES} margin={{ top: 5, right: 10, bottom: 30, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis dataKey="name" tick={{ fill: theme.textMuted, fontSize: 9, angle: -35, textAnchor: "end" }} />
              <YAxis domain={[0, 120]} tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={v => `${v}%`} />
              <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, borderRadius: "8px" }} formatter={(v, n) => [`${v}%`, n]} />
              <Legend wrapperStyle={{ fontSize: "11px" }} />
              <Bar dataKey="utilization" name="Utilization" radius={[4,4,0,0]}>
                {DEMO_RESOURCES.map((r, i) => <Cell key={i} fill={utilizationColor(r.utilization)} />)}
              </Bar>
              <Bar dataKey="efficiency"  name="Efficiency" fill={`${theme.cyan}88`} radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Efficiency vs Utilization Scatter */}
        <div className="pmo-card">
          <div className="section-title">Efficiency vs Utilization</div>
          <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "6px" }}>Click a resource for detail</div>
          <ResponsiveContainer width="100%" height={220}>
            <ScatterChart margin={{ top: 10, right: 10, bottom: 20, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${theme.cardBorder}66`} />
              <XAxis type="number" dataKey="x" name="Utilization" domain={[30, 120]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "Utilization %", fill: theme.textMuted, fontSize: 10, position: "bottom", offset: -5 }} />
              <YAxis type="number" dataKey="y" name="Efficiency" domain={[60, 105]}
                tick={{ fill: theme.textMuted, fontSize: 9 }}
                label={{ value: "Efficiency %", angle: -90, fill: theme.textMuted, fontSize: 10 }} />
              <ZAxis range={[30, 80]} />
              <Tooltip content={({ payload }) => {
                if (!payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: theme.card, border: `1px solid ${theme.cardBorder}`, padding: "8px 12px", borderRadius: "8px", fontSize: "11px" }}>
                    <div style={{ color: d.bottleneck ? theme.danger : theme.accent, fontWeight: 600 }}>{d.name}</div>
                    <div>Util: {d.x}% · Eff: {d.y}%</div>
                    {d.bottleneck && <div style={{ color: theme.danger }}>⚠ BOTTLENECK</div>}
                  </div>
                );
              }} />
              <Scatter data={scatterData}
                shape={(props) => {
                  const { cx, cy, payload } = props;
                  const color = payload.bottleneck ? theme.danger : payload.x > 100 ? theme.warning : theme.success;
                  return (
                    <g onClick={() => setSelected(DEMO_RESOURCES.find(r => r.name === payload.name))} style={{ cursor: "pointer" }}>
                      <circle cx={cx} cy={cy} r={7} fill={`${color}55`} stroke={color} strokeWidth={1.5} />
                      {payload.bottleneck && <text x={cx} y={cy - 10} textAnchor="middle" fill={theme.danger} fontSize="8">⚠</text>}
                    </g>
                  );
                }}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Resource Detail Panel */}
      {selected && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: selected.bottleneck ? theme.danger : theme.accent }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div className="section-title" style={{ marginBottom: 0 }}>{selected.name}</div>
              {selected.bottleneck && <span style={{ padding: "2px 8px", borderRadius: "10px", background: `${theme.danger}20`, color: theme.danger, fontSize: "10px", fontWeight: 700 }}>BOTTLENECK</span>}
            </div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "16px" }}>
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={`${theme.cardBorder}88`} />
                <PolarAngleAxis dataKey="subject" tick={{ fill: theme.textMuted, fontSize: 9 }} />
                <PolarRadiusAxis angle={90} domain={[0, 120]} tick={false} />
                <Radar dataKey="A" stroke={selected.bottleneck ? theme.danger : theme.accent} fill={selected.bottleneck ? theme.danger : theme.accent} fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              {[
                { l: "Role",         v: selected.role },
                { l: "Department",   v: selected.dept },
                { l: "Utilization",  v: `${selected.utilization}%`, c: utilizationColor(selected.utilization) },
                { l: "Efficiency",   v: `${selected.efficiency}%`,  c: selected.efficiency >= 90 ? theme.success : theme.warning },
                { l: "Active Projects", v: selected.projects },
                { l: "Cost Rate",    v: `$${selected.cost_rate}/hr` },
                { l: "ML Recommendation", v: selected.bottleneck
                    ? "Immediate workload redistribution required. Consider adding a parallel resource or deferring non-critical tasks."
                    : selected.utilization > 90
                    ? "Monitor closely. Approaching capacity threshold."
                    : "Resource availability adequate. Can absorb additional work.",
                  span: true, c: selected.bottleneck ? theme.danger : theme.textSecondary },
              ].filter(Boolean).map((item, i) => (
                <div key={i} style={item.span ? { gridColumn: "1/-1", padding: "8px 12px", background: `${item.c || theme.accent}10`, border: `1px solid ${item.c || theme.accent}33`, borderRadius: "6px" } : {}}>
                  <div style={{ fontSize: "10px", color: theme.textMuted, marginBottom: "3px" }}>{item.l}</div>
                  <div style={{ fontSize: "13px", color: item.c || theme.textPrimary, fontWeight: 500 }}>{item.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Resource Table */}
      <div className="pmo-card">
        <div className="section-title">Resource Register</div>
        <table>
          <thead>
            <tr>{["Name","Role","Dept","Utilization","Efficiency","Projects","Cost Rate/hr","Bottleneck","ML Recommendation"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEMO_RESOURCES.sort((a, b) => b.utilization - a.utilization).map((r, i) => (
              <tr key={i} style={{ cursor: "pointer" }} onClick={() => setSelected(r)}>
                <td style={{ color: theme.textPrimary, fontWeight: 500 }}>{r.name}</td>
                <td>{r.role}</td>
                <td>{r.dept}</td>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "60px", height: "4px", background: theme.cardBorder, borderRadius: "2px" }}>
                      <div style={{ width: `${Math.min(r.utilization, 100)}%`, height: "100%", background: utilizationColor(r.utilization), borderRadius: "2px" }} />
                    </div>
                    <span style={{ color: utilizationColor(r.utilization), fontFamily: "'Share Tech Mono',monospace", fontSize: "12px" }}>{r.utilization}%</span>
                  </div>
                </td>
                <td style={{ color: r.efficiency >= 90 ? theme.success : theme.warning, fontFamily: "'Share Tech Mono',monospace" }}>{r.efficiency}%</td>
                <td style={{ textAlign: "center" }}>{r.projects}</td>
                <td style={{ fontFamily: "'Share Tech Mono',monospace" }}>${r.cost_rate}/hr</td>
                <td>{r.bottleneck ? <span style={{ padding: "2px 8px", borderRadius: "10px", background: `${theme.danger}20`, color: theme.danger, fontSize: "10px", fontWeight: 700 }}>BOTTLENECK</span> : <span style={{ color: theme.textMuted, fontSize: "11px" }}>OK</span>}</td>
                <td style={{ fontSize: "11px", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.bottleneck ? "Redistribute workload immediately" : r.utilization > 90 ? "Monitor — approaching limit" : "Available for allocation"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
